import type { Row } from "@tanstack/react-table"

import type { TreeNode } from "../types"

import type { Location } from "@/graphql/codegen/graphql"
import colors from "@/styles/colors"
import { AddressFormat } from "@/types/common"
import type { LabelMapping, RouteNavObject } from "@/types/routes"

export const upperSnakeCaseToCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    .replace(/^[a-z]/, (match) => match.toLowerCase())
}

export const toUpperSnakeCase = (str: string): string => {
  return str.replace(/([A-Z])/g, "_$1").toUpperCase()
}

export const normalizeToUpperCase = (str: string): string => {
  return str.trim().toUpperCase()
}

export const sortRoutesByLabelMapping = (routes: RouteNavObject[], labelMapping: LabelMapping): RouteNavObject[] => {
  return routes.sort((a, b) => {
    const indexA = Object.keys(labelMapping).indexOf(a.pathname)
    const indexB = Object.keys(labelMapping).indexOf(b.pathname)
    return indexA - indexB
  })
}

export const stringAvatar = (name: string): { sx: { backgroundColor: string; color: string }; children: string } => {
  return {
    sx: {
      backgroundColor: colors.primary["400"],
      color: "white",
    },
    children: name?.length > 0 ? `${name.split(" ")[0][0]}${name.split(" ")[1][0]}` : "",
  }
}

export const uniqueArrayOfObjects = (arr: Record<string, any>[], key: string): Record<string, any>[] =>
  arr.reduce((acc, curr) => {
    if (!acc.some((item: Record<string, any>) => item[key] === curr[key])) {
      acc.push(curr)
    }
    return acc
  }, []) as Record<string, any>[]

export const formatLocationAddress = (location?: Location | null, format?: AddressFormat): string => {
  if (!location) {
    return ""
  }

  let parts: (string | null | undefined)[]
  const {
    address1,
    address2,
    city,
    country,
    description,
    externalCompanyId,
    externalLocationId,
    externalLocationType,
    name,
    postalCode,
    region,
  } = location

  const address = [address1, address2].filter(Boolean).join(" ")

  const regionIsoCode = region?.shortIsoCode ?? region?.isoCode ?? location.regionIsoCode

  const externalIds = [externalCompanyId, externalLocationId].filter(Boolean).join(" - ")

  const externalIdsType = [externalIds, externalLocationType].filter(Boolean).join(" ")

  const descriptionExternalIdsType = [description, externalIdsType].filter(Boolean).join(" ")

  switch (format) {
    case AddressFormat.Long:
      parts = [descriptionExternalIdsType, address1, city, regionIsoCode, country?.name]
      break
    case AddressFormat.Medium:
      parts = [descriptionExternalIdsType, city, regionIsoCode]
      break
    case AddressFormat.Short:
      parts = [externalIdsType]
      break
    case AddressFormat.VeryShort:
      parts = [externalIds]
      break
    case AddressFormat.AddressOnly:
      parts = [address, city, regionIsoCode, country?.name, postalCode]
      break
    case AddressFormat.LocationAndAddress:
      parts = [description, address, city, regionIsoCode, country?.name, postalCode]
      break
    case AddressFormat.LocationDescription:
      parts = [description]
      break
    case AddressFormat.Shipping:
      parts = [description, city, regionIsoCode]
      break
    case AddressFormat.BillTo:
      parts = [address1, city, regionIsoCode]
      break
    default:
      parts = [name, location.externalLocationId, city, regionIsoCode]
      break
  }

  return parts.filter(Boolean).join(", ")
}

export const booleanToString = (value?: boolean | null): string => {
  if (value === null || value === undefined) {
    return ""
  } else {
    return value ? "True" : "False"
  }
}

export const booleanToYesNoString = (value?: boolean | null): string => {
  if (value === null || value === undefined) {
    return ""
  } else {
    return value ? "Yes" : "No"
  }
}

export const isTableRow = <T>(value: unknown): value is Row<T> => {
  return value !== null && typeof value === "object" && "original" in value
}

export const classNames = (...classes: (string | Record<string, boolean>)[]): string => {
  return classes
    .flatMap((cls) => {
      if (typeof cls === "string") {
        return cls
      }
      return Object.entries(cls).map(([key, value]) => (value ? key : ""))
    })
    .filter(Boolean)
    .join(" ")
}

export function flattenTree(
  nodes: TreeNode<string>[],
  depth = 0
): { value: string; label: string; parentId: string | null | undefined; depth: number }[] {
  return nodes.flatMap((node) => {
    const { children, ...rest } = node
    const self = { value: rest.id, label: rest.name, parentId: rest.parent_id, depth }
    const kids = children?.length ? flattenTree(children, depth + 1) : []
    return [self, ...kids]
  })
}

export function stripCountryCodeAndSpacesFromPhoneNumber(phoneNumber: string | undefined): string | undefined {
  if (!phoneNumber) {
    return undefined
  }

  // Remove the country code (assuming it starts with '+<digits>')
  const withoutCountryCode = phoneNumber.indexOf(" ") > 0 ? phoneNumber.replace(/^\+\d+\s*/, "") : phoneNumber
  // Remove any remaining spaces
  const withoutSpaces = withoutCountryCode.replace(/\s+/g, "")
  return withoutSpaces
}

export const isNil = (value: unknown): value is null | undefined => value === null || value === undefined
